// src/tenantAdmin/pages/ManageUsersPage.jsx
// The "Utenti" tab of Gestione Workspace. Self-contained presentation (no shared
// AdminTable/AdminPagination — those still back the SA console): load/paginate/
// search, role change, deactivate, remove, plus the pencil-in-NAME edit modal
// (TenantEditUserModal).
//
// ⛔⛔ The page acts on the SELECTED workspace, not the active one. Every
// tenantAdminApi call and both modals take that id, so what the header names and
// what a write lands in can never diverge. The selection is DERIVED every render
// (`chosen === null` = "not chosen yet"): a snapshot would survive a claims
// refresh that took the workspace away.
//
// ⛔ The page does NOT render the page title — TenantAdminLayout owns
// `manage.title` and the tab bar. The card heading here is the card's own.
//
// ⛔⛔ THREE STATES, NOT TWO. `is_active: false` means either "never chose a
// credential" (`pending_invite`) or "was switched off", and the remedy differs —
// a fresh invitation LINK versus a reactivation. UserStatusPill labels them apart
// and UserRowActions offers exactly one control per state; this page only carries
// out what they ask for. See both components for why the wrong offer is worse
// than none.
import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useOutletContext } from 'react-router-dom';
import { Users, UserPlus, Search, Pencil, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import ConfirmDialog from '../../admin/components/ConfirmDialog.jsx';
import { useAuth } from '../../auth/AuthContext.jsx';
import { COLORS, FONT_SANS, RADII, SEMANTIC, SHADOW } from '../../constants/theme.js';
import {
  deactivateTenantUser,
  changeTenantUserRole,
  removeTenantUser,
  resendTenantInvite,
  activateTenantUser,
  correctTenantUserEmail,
} from '../tenantAdminApi.js';
import { canSend, record } from '../resendThrottle.js';
import { membershipRefusalCopy } from '../membershipRefusal.js';
import { userActionErrorCopy } from '../userActionError.js';
import useTenantUsers from '../hooks/useTenantUsers.js';
import TenantCreateUserModal from './TenantCreateUserModal.jsx';
import TenantEditUserModal from './TenantEditUserModal.jsx';
import UserStatusPill from '../components/UserStatusPill.jsx';
import UserRowActions from '../components/UserRowActions.jsx';
import CorrectEmailDialog from '../components/CorrectEmailDialog.jsx';
import WorkspaceSelector from '../components/WorkspaceSelector.jsx';
import SeatTile from '../components/SeatTile.jsx';
import OutcomeDialog from '../../components/ui/OutcomeDialog.jsx';
import CompanyAdminsBlock from '../components/CompanyAdminsBlock.jsx';
import CompanyAdminsSection from '../components/CompanyAdminsSection.jsx';
import useWorkspaceCompany from '../hooks/useWorkspaceCompany.js';

const LIMIT = 20;

const fieldBase = {
  height: '46px', boxSizing: 'border-box',
  background: COLORS.surface, border: `1px solid ${COLORS.border}`,
  borderRadius: RADII.control, color: COLORS.ink,
  fontSize: '13px', fontFamily: FONT_SANS, outline: 'none',
};
const th = {
  padding: '12px 24px', textAlign: 'left',
  fontSize: '11px', fontWeight: 600, letterSpacing: '0.6px', textTransform: 'uppercase',
  color: COLORS.textSecondary, fontFamily: FONT_SANS, whiteSpace: 'nowrap',
};
const td = {
  padding: '14px 24px', fontSize: '13px', color: COLORS.ink,
  fontFamily: FONT_SANS, borderTop: `1px solid ${COLORS.border}`,
};

export default function ManageUsersPage() {
  const { t } = useTranslation('admin');
  const { activeTenant, adminTenants, user } = useAuth();
  const [offset, setOffset] = useState(0);
  const [emailFilter, setEmailFilter] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  // The row-scoped dialogs. Each holds the ROW it was opened on, so the
  // write can never be aimed at whatever the table shows by the time it lands.
  const [resendTarget, setResendTarget] = useState(null);
  const [emailTarget, setEmailTarget] = useState(null);
  const [deactivateTarget, setDeactivateTarget] = useState(null);
  // What the last action did, shown in OutcomeDialog until «OK». ⛔ NEVER the
  // server's `warning` string (fixed English prose): every sentence is worded
  // here. ⚠️ Set AFTER the re-read on the paths that run inside a dialog, so the
  // outcome opens as that dialog closes, never over it.
  const [outcome, setOutcome] = useState(null);
  // `null` = the user has not chosen; the default below then applies. ⛔ Not
  // seeded into state at mount: the claims can change under the page.
  const [chosen, setChosen] = useState(null);

  // Defensive: any consumer (and every test mock) may omit `adminTenants`.
  const options = adminTenants || [];
  const activeId = activeTenant?.tenant_id;
  // The company the ACTIVE workspace belongs to, when the user administers it
  // (null otherwise, and `undefined` context outside the console — hence `?.`).
  // ⛔ A plain workspace admin has no Piani tab, and the standalone plans page
  // only tells them "not for you" — so they get no link at all. This tab is NOT
  // behind ActiveCompanyGuard, so while the company read is still in flight the
  // id is null and the tile is briefly linkless — the benign side.
  const activeCompanyId = useOutletContext()?.company?.activeCompanyId || null;
  // Default: the ACTIVE workspace when the user administers it (that is the one
  // the rest of the app is pointed at), else the first administered one.
  const fallbackId = options.some(w => w.tenant_id === activeId) ? activeId : options[0]?.tenant_id;
  const tenantId = options.some(w => w.tenant_id === chosen) ? chosen : fallbackId;
  const selectedTenant = options.find(w => w.tenant_id === tenantId) || null;
  const currentUserId = user?.user_id;
  // The workspace on screen NOW, for writes that leave the selector free (no
  // dialog): an answer that lands after a switch belongs to the old workspace, so
  // it opens no outcome over the new one.
  const tenantRef = useRef(tenantId);
  tenantRef.current = tenantId;

  const {
    users, companyAdmins, total, loading, error, seats, reload, reloadSeats,
  } = useTenantUsers(tenantId, { limit: LIMIT, offset, email: emailFilter });
  // v202 — the company that owns the SELECTED workspace, when the user administers it: its administrators are
  // managed in a section below the users. undefined = still looking it up (render nothing there); null = no
  // section (the read-only block shows them instead).
  const adminCompanyId = useWorkspaceCompany(tenantId);
  // An add or a removal moves the company's seat count AND may change how a member's row reads (`via`).
  const onAdminsChanged = () => { reload(); reloadSeats(); };

  // ⛔ Any open dialog or confirm FREEZES the selector: the workspace on screen is
  // the workspace the dialog's action lands in.
  const dialogOpen = showCreate || !!removeTarget || !!editTarget
    || !!resendTarget || !!emailTarget || !!deactivateTarget;

  // A workspace change starts from page 1 with no search: an offset and a filter
  // are properties of the list being looked at, and `offset=40` on a workspace
  // with three users shows an empty table.
  // An outcome belongs to the workspace it happened in, so it goes too.
  useEffect(() => { setEmailFilter(''); setOffset(0); setOutcome(null); }, [tenantId]);
  useEffect(() => { setOffset(0); }, [emailFilter]);

  function handleSearch(e) {
    e.preventDefault();
    setOffset(0);
  }

  async function handleChangeRole(row, newRole) {
    if (newRole === row.role) return;
    const at = tenantId;
    try {
      await changeTenantUserRole(at, row.user_id, newRole);
      if (tenantRef.current === at) await reload();
    } catch (err) {
      if (tenantRef.current === at) setOutcome({ tone: 'warning', text: userActionErrorCopy(t, err) });
    }
  }

  async function confirmRemove() {
    if (!removeTarget) return;
    // ConfirmDialog owns the error path (await + inline error banner) and renders
    // whatever the rejection carries as its sentence — so the refusal is worded
    // HERE and rethrown, never handed to the dialog as the server sent it. The
    // dialog is shared by a dozen screens and is deliberately left untouched.
    // On success it calls onClose, which clears removeTarget.
    const target = removeTarget;
    let res;
    try {
      res = await removeTenantUser(tenantId, target.user_id);
    } catch (err) {
      throw new Error(userActionErrorCopy(t, err));
    }
    // ⭐ `account_deleted: true` (BE 7c5dc05): the person never set a password and
    // this was their last workspace or company, so the backend deleted the ACCOUNT
    // and its link too. Say "invitation withdrawn", not "removed" — and re-inviting
    // the address is now an ordinary invite. The row goes with the re-read below
    // (it is gone server-side either way); ⛔ never infer this from the row's chip.
    // Re-read the count: a removal can free a company seat — but only if this
    // was the person's last workspace in the company, which the envelope knows
    // and we do not.
    await Promise.all([reload(), reloadSeats()]);
    if (res?.account_deleted === true) {
      setOutcome({ tone: 'info', text: t('tenantUsers.invitationWithdrawn', { email: target.email }) });
    }
  }

  // Run by the «Disattiva» ConfirmDialog, which renders whatever we throw — so
  // a refusal is worded HERE and the dialog stays open on it.
  async function confirmDeactivate() {
    if (!deactivateTarget) return;
    try {
      await deactivateTenantUser(tenantId, deactivateTarget.user_id);
    } catch (err) {
      // The two "belongs elsewhere" 409s are worded by membershipRefusal.js, in
      // the deactivate remedy (remove them from this workspace, or ask a platform
      // administrator) — never from the server's own sentence, never listing the
      // returned IDs. Everything else falls to userActionError.js, which is still
      // our copy.
      throw new Error(membershipRefusalCopy(t, err, 'deactivate') || userActionErrorCopy(t, err));
    }
    // ⚠️ Deactivating FREES a company seat (the backend counts a person while
    // active or still owing an onboarding link), so the tile is re-read too.
    reload();
    reloadSeats();
  }

  // ── The invitation link ─────────────────────────────────────────────────────
  // ⛔ Refused BEFORE the confirm when this browser has already sent three links
  // to the address inside ten minutes. The confirm promises a new link AND the
  // death of the old one; after a 429 neither happened, so the promise must not
  // be made. The server's own 429 stays mapped as the authority (resendThrottle.js).
  function handleResendClick(row) {
    if (!canSend(row.email)) {
      setOutcome({ tone: 'warning', text: t('manage.resendThrottled') });
      return;
    }
    setResendTarget(row);
  }

  async function confirmResend() {
    if (!resendTarget) return;
    const { email } = resendTarget;
    let res;
    try {
      res = await resendTenantInvite(tenantId, resendTarget.user_id);
    } catch (err) {
      // ConfirmDialog renders whatever we throw, so the refusal is worded HERE —
      // membership refusal first (a resend writes the shared ACCOUNT, so this
      // workspace cannot unblock it), then the code table.
      throw new Error(membershipRefusalCopy(t, err, 'resend') || userActionErrorCopy(t, err));
    }
    // The request was accepted, so it spent the address's server-side budget —
    // record it whether or not the mail went out.
    record(email);
    // ⛔⛔ `email_sent: false` IS NOT SUCCESS. The link was rotated (the previous
    // one is dead now) and nothing was delivered, so the person is worse off than
    // before. Never word this as "sent".
    await reload();
    setOutcome(res?.email_sent === false
      ? { tone: 'warning', text: t('manage.resendNotDelivered') }
      : { tone: 'success', text: t('manage.resendDone', { email }) });
  }

  // ── Reactivation ────────────────────────────────────────────────────────────
  // Only ever offered on a row that ALREADY chose a credential (UserRowActions
  // gates it): there is nothing to restore for someone who never onboarded, and
  // the backend says so with 409 USER_NEVER_ONBOARDED.
  async function handleReactivate(row) {
    const at = tenantId;
    try {
      const res = await activateTenantUser(at, row.user_id);
      if (tenantRef.current !== at) return;
      // `already_active` is not a refusal — our row was stale. Say what is true
      // rather than claiming a change nobody made.
      setOutcome(res?.status === 'already_active'
        ? { tone: 'info', text: t('tenantUsers.alreadyActive') }
        : { tone: 'success', text: t('manage.reactivateDone', { email: row.email }) });
      // A reactivation TAKES A SEAT BACK, so the tile is re-read with the list.
      reload();
      reloadSeats();
    } catch (err) {
      if (tenantRef.current !== at) return;
      setOutcome({
        tone: 'warning',
        text: membershipRefusalCopy(t, err, 'activate') || userActionErrorCopy(t, err),
      });
      // 409 USER_SEAT_LIMIT_REACHED means our figure was behind the company's.
      reloadSeats();
    }
  }

  // ── The sign-in address ─────────────────────────────────────────────────────
  // ⛔ Rethrows: CorrectEmailDialog owns the error surface (it words the refusal
  // and stays open on the draft). This only runs on the success path.
  async function submitCorrectEmail(nextEmail) {
    const row = emailTarget;
    const res = await correctTenantUserEmail(tenantId, row.user_id, nextEmail);
    // The SERVER says which pair actually changed; the form only proposed one.
    const previous = res?.previous_email || row.email;
    const email = res?.email || nextEmail;
    // An invited person's link encodes the OLD address, so the correction killed
    // it. The outcome that reports the change carries the remedy with it.
    const wasInvited = !row.is_active && !!row.pending_invite;
    await reload();
    setOutcome({
      tone: 'success',
      text: t(wasInvited ? 'manage.correctEmailDoneInvited' : 'manage.correctEmailDone', { previous, email }),
      action: wasInvited
        ? { label: t('manage.resend'), onClick: () => handleResendClick({ ...row, email }) }
        : undefined,
    });
  }

  // The add-user dialog reports WHAT HAPPENED; this turns it into one outcome.
  // ⛔ No row and no "invited" chip is ever synthesised here: on the invitation
  // paths the membership does not exist yet, and a reload is the only truth.
  function handleCreated(created) {
    // Always reload: 'attached' adds a row and a seat, and the two invitation
    // paths may still have changed the count from another tab.
    reload();
    reloadSeats();
    if (!created || !created.kind) { setOutcome(null); return; }
    const { kind, email, emailSent, nameNotSaved } = created;
    // ⚠️ The first invitation counts against the SAME per-address budget as a
    // resend (the backend shares one limit across invite, attach and resend), so
    // it is recorded here too — otherwise three resends would look allowed right
    // after the invite that already spent one.
    if (email) record(email);
    let tone = 'info';
    let text;
    if (kind === 'attached') {
      tone = 'success';
      text = t('tenantUsers.userAdded', { email });
    } else if (kind === 'invited') {
      tone = emailSent ? 'info' : 'warning';
      text = emailSent
        ? t('tenantUsers.invitationSent', { email })
        : t('tenantUsers.invitationNotDelivered', { email });
    } else {
      tone = emailSent ? 'info' : 'warning';
      text = emailSent
        ? t('tenantUsers.userInvited', { email })
        : t('tenantUsers.userInviteNotDelivered', { email });
      // The account exists either way — the name is the only thing that did not
      // land, so this is an addition to the outcome, never a replacement.
      if (nameNotSaved) text = `${text} ${t('tenantUsers.nameNotSaved')}`;
    }
    setOutcome({ tone, text });
  }

  // No administered workspace: there is nothing for this tab to act on. The tab
  // is not offered in that case (TenantAdminLayout builds it from `adminTenants`)
  // — this only catches a hand-typed URL.
  if (!selectedTenant) return null;

  const pageCount = Math.max(1, Math.ceil(total / LIMIT));
  const current = Math.floor(offset / LIMIT) + 1;
  const from = total === 0 ? 0 : offset + 1;
  const to = Math.min(offset + LIMIT, total);

  const pageBtn = (active) => ({
    width: '32px', height: '32px', borderRadius: RADII.pill,
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    border: active ? 'none' : `1px solid ${COLORS.border}`,
    background: active ? COLORS.accent : COLORS.surface,
    color: active ? COLORS.surface : COLORS.ink,
    fontSize: '13px', fontWeight: 600, fontFamily: FONT_SANS,
    cursor: 'pointer',
  });

  return (
    <div style={{ padding: '32px 40px', fontFamily: FONT_SANS, color: COLORS.ink, maxWidth: '1280px', margin: '0 auto' }}>
      <style>{`.user-row:hover { background: ${COLORS.field}; }`}</style>

      {error && (
        <div style={{
          marginBottom: '16px', padding: '12px 16px', borderRadius: RADII.control,
          background: SEMANTIC.errore.bg, border: `1px solid ${SEMANTIC.errore.fg}33`,
          color: SEMANTIC.errore.fg, fontSize: '13px',
        }}>
          {error}
        </div>
      )}

      <div style={{
        background: COLORS.surface, border: `1px solid ${COLORS.border}`,
        borderRadius: RADII.section, boxShadow: SHADOW.card, overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '24px 24px 0', flexWrap: 'wrap' }}>
          <div style={{
            width: '40px', height: '40px', borderRadius: RADII.pill, flexShrink: 0,
            border: `1px solid ${COLORS.border}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Users size={20} color={COLORS.ink} strokeWidth={1.8} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '16px', fontWeight: 700, lineHeight: '24px' }}>{t('users.title')}</div>
            <div style={{ fontSize: '12px', color: COLORS.textTertiary }}>
              {t('tenantUsers.subtitle', { name: selectedTenant.display_name })}
            </div>
          </div>
          <WorkspaceSelector value={tenantId} onChange={setChosen} disabled={dialogOpen} />
          {/* ⛔ No `disabled`, at any seat figure — see SeatTile. */}
          <button
            onClick={() => setShowCreate(true)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '8px',
              height: '46px', padding: '0 20px', borderRadius: RADII.control, border: 'none',
              background: COLORS.accent,
              color: COLORS.surface, fontSize: '13px',
              fontWeight: 600, cursor: 'pointer', fontFamily: FONT_SANS,
            }}
          >
            <UserPlus size={16} /> {t('tenantUsers.createUser')}
          </button>
        </div>

        {/* Seats — the COMPANY's people against its ceiling. Both figures come
            from the user-list envelope (fetchTenantSeats), so the search box
            below can never move them. The remedy link points at the console's own
            Piani tab, and is withheld unless this IS the active workspace AND its
            company is one the user administers — see SeatTile. */}
        <SeatTile
          seats={seats}
          showBillingLink={tenantId === activeId && !!activeCompanyId}
          billingTo="/tenant-admin/manage/plans"
        />

        {/* Search */}
        <div style={{ padding: '20px 24px 24px' }}>
          <form onSubmit={handleSearch}>
            <div style={{ position: 'relative', maxWidth: '320px' }}>
              <Search size={16} color={COLORS.textSecondary} style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
              <input
                type="text"
                placeholder={t('users.searchPlaceholder')}
                value={emailFilter}
                onChange={e => setEmailFilter(e.target.value)}
                style={{ ...fieldBase, width: '100%', padding: '0 14px 0 40px' }}
              />
            </div>
          </form>
        </div>

        {/* Table */}
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: COLORS.field }}>
              <th style={th}>{t('shared.name')}</th>
              <th style={th}>{t('shared.email')}</th>
              <th style={th}>{t('shared.status')}</th>
              <th style={th}>{t('tenantUsers.roleInTenant')}</th>
              <th style={th}>{t('users.columns.createdAt')}</th>
              <th style={{ ...th, textAlign: 'right' }} />
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td style={{ ...td, textAlign: 'center', color: COLORS.textTertiary }} colSpan={6}>{t('shared.loading')}</td>
              </tr>
            )}
            {!loading && users.length === 0 && (
              <tr>
                <td style={{ ...td, textAlign: 'center', color: COLORS.textTertiary }} colSpan={6}>{t('users.empty')}</td>
              </tr>
            )}
            {!loading && users.map(row => {
              const isSelf = row.user_id === currentUserId;
              return (
                <tr key={row.user_id} className="user-row" style={{ transition: 'background 0.15s' }}>
                  <td style={td}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <button
                        type="button"
                        aria-label={t('tenantUsers.editAriaLabel', { email: row.email })}
                        title={t('tenantUsers.editUser')}
                        onClick={() => setEditTarget(row)}
                        style={{
                          width: '32px', height: '32px', borderRadius: RADII.pill, flexShrink: 0,
                          border: `1px solid ${COLORS.border}`, background: COLORS.surface, cursor: 'pointer',
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        }}
                      >
                        <Pencil size={14} color={COLORS.textSecondary} />
                      </button>
                      {/* Name is nullable; show a dash rather than duplicating the
                          email column (userLabel would fall back to email here). */}
                      <span>{[row.first_name, row.last_name].filter(Boolean).join(' ') || '—'}</span>
                    </div>
                  </td>
                  <td style={{ ...td, color: COLORS.textSecondary }}>{row.email}</td>
                  <td style={td}>
                    <UserStatusPill isActive={row.is_active} pendingInvite={row.pending_invite} />
                  </td>
                  <td style={td}>
                    <div style={{ position: 'relative', width: '160px' }}>
                      <select
                        value={row.role || 'editor'}
                        disabled={isSelf}
                        title={isSelf ? t('tenantUsers.cannotChangeOwnRole') : undefined}
                        onChange={(e) => handleChangeRole(row, e.target.value)}
                        style={{
                          ...fieldBase, height: '40px', width: '100%', padding: '0 40px 0 14px',
                          appearance: 'none', cursor: isSelf ? 'not-allowed' : 'pointer',
                        }}
                      >
                        <option value="viewer">viewer</option>
                        <option value="editor">editor</option>
                        <option value="admin">admin</option>
                      </select>
                      <ChevronDown size={16} color={COLORS.textTertiary} style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
                    </div>
                  </td>
                  <td style={{ ...td, color: COLORS.textSecondary, whiteSpace: 'nowrap' }}>
                    {row.created_at ? new Date(row.created_at).toLocaleDateString('it-IT') : '—'}
                  </td>
                  <td style={{ ...td, textAlign: 'right' }}>
                    {/* Which controls this row earns is decided by its STATE, in
                        UserRowActions — never here. `seats` only ever disables
                        Riattiva, and only at a KNOWN cap. */}
                    <UserRowActions
                      row={row}
                      isSelf={isSelf}
                      seats={seats}
                      onResend={handleResendClick}
                      onReactivate={handleReactivate}
                      onDeactivate={setDeactivateTarget}
                      onCorrectEmail={setEmailTarget}
                      onRemove={setRemoveTarget}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {/* Footer — count + numbered pagination */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: '16px', padding: '16px 24px', borderTop: `1px solid ${COLORS.border}`,
        }}>
          <span style={{ fontSize: '13px', color: COLORS.textSecondary }}>
            {t('tenantUsers.paginationRange', { from, to, total })}
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button
              type="button"
              aria-label={t('pagination.prevPage')}
              disabled={current <= 1}
              onClick={() => setOffset(Math.max(0, offset - LIMIT))}
              style={{ ...pageBtn(false), opacity: current <= 1 ? 0.4 : 1, cursor: current <= 1 ? 'default' : 'pointer' }}
            >
              <ChevronLeft size={16} />
            </button>
            {Array.from({ length: pageCount }, (_, i) => i + 1).map(p => (
              <button
                key={p}
                type="button"
                aria-current={p === current ? 'page' : undefined}
                onClick={() => setOffset((p - 1) * LIMIT)}
                style={pageBtn(p === current)}
              >
                {p}
              </button>
            ))}
            <button
              type="button"
              aria-label={t('pagination.nextPage')}
              disabled={current >= pageCount}
              onClick={() => setOffset(offset + LIMIT)}
              style={{ ...pageBtn(false), opacity: current >= pageCount ? 0.4 : 1, cursor: current >= pageCount ? 'default' : 'pointer' }}
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* The company's administrators. A company admin MANAGES them here (the section, for the company of the
          selected workspace; an add or a removal moves the seat count). Anyone else sees them read-only, once
          per list: the backend appends them to every page and ignores the search for them, so page 1 with no
          filter is the only place they can appear without lying. */}
      {/* ⚠️ The section's own dialogs are full-screen overlays, so the selector (frozen by `dialogOpen` for this
          page's dialogs) cannot be reached while one of them is open either. */}
      {adminCompanyId && <CompanyAdminsSection companyId={adminCompanyId} onChanged={onAdminsChanged} />}
      {adminCompanyId === null && offset === 0 && !emailFilter && <CompanyAdminsBlock rows={companyAdmins} />}

      <TenantCreateUserModal
        open={showCreate}
        tenantId={tenantId}
        tenantName={selectedTenant.display_name}
        onClose={() => setShowCreate(false)}
        onCreated={handleCreated}
      />

      <TenantEditUserModal
        open={!!editTarget}
        tenantId={tenantId}
        user={editTarget}
        currentUserId={currentUserId}
        onClose={() => setEditTarget(null)}
        onSaved={() => reload()}
      />

      <CorrectEmailDialog
        open={!!emailTarget}
        currentEmail={emailTarget?.email}
        invited={!!emailTarget && !emailTarget.is_active && !!emailTarget.pending_invite}
        onSubmit={submitCorrectEmail}
        onClose={() => setEmailTarget(null)}
      />

      {/* The resend confirm says the previous link STOPS WORKING — the one fact
          about a resend that cannot be undone. */}
      <ConfirmDialog
        open={!!resendTarget}
        onClose={() => setResendTarget(null)}
        onConfirm={confirmResend}
        title={t('manage.resendConfirmTitle')}
        message={t('manage.resendConfirm', { email: resendTarget?.email })}
        confirmLabel={t('manage.resend')}
      />

      <ConfirmDialog
        open={!!removeTarget}
        onClose={() => setRemoveTarget(null)}
        onConfirm={confirmRemove}
        title={t('tenantUsers.removeTitle')}
        message={t(
          // An invited person never signed in, and their account may go with the
          // removal (account_deleted) — so their sentence claims neither.
          removeTarget && !removeTarget.is_active && removeTarget.pending_invite
            ? 'tenantUsers.removeMessageInvited' : 'tenantUsers.removeMessage',
          { email: removeTarget?.email, tenant: selectedTenant.display_name },
        )}
        confirmLabel={t('shared.remove')}
        variant="danger"
      />

      <ConfirmDialog
        open={!!deactivateTarget}
        onClose={() => setDeactivateTarget(null)}
        onConfirm={confirmDeactivate}
        title={t('tenantUsers.deactivateTitle')}
        message={t('tenantUsers.deactivateConfirm', { email: deactivateTarget?.email })}
        confirmLabel={t('users.deactivate')}
        variant="danger"
      />

      <OutcomeDialog outcome={outcome} onClose={() => setOutcome(null)} />
    </div>
  );
}
